import React from 'react';
import {
  Box, Card, Grid, Typography,
} from '@mui/material';
import ViewTitle from '../../../components/ViewTitle';
import BnplPurchasingPower from '../../../components/Bnpl/BnplPurchasingPower';
import BnplNextMonthSummaryCard from '../../../components/Bnpl/BnplNextMonthSummaryCard';
import BnplMovements from '../../../components/Bnpl/BnplMovements';
import MisCuponesCard from '../../../components/Bnpl/MisCuponesCard';
import { getBnplNextPayment } from '../../../components/Bnpl/bnplNextPayment';
import type { BnplNextPaymentKind } from '../../../components/Bnpl/bnplNextPayment';
import type { LoanOverViewType, UserAccountComponent } from '../../../types/account';
import accountStyles, { sx } from '../styles';
import bnplPageStyles from './style';
import { getLoan } from '../../../queries/account';
import { getBnplPurchasingPower } from '../../../queries/bnpl';
import { getUserCoupons } from '../../../queries/coupons';
import LoanOverview from '../../../feature/account/LoanOverview';
import analytics from '../../../utils/analytics';

const NEXT_PAYMENT_LABELS: Record<BnplNextPaymentKind, string> = {
  inProcess: 'Pago en proceso',
  overdue: 'Monto vencido por pagar',
  nextMonth: 'Total a pagar próximo mes',
};

const IN_PROCESS_HELPER_TEXT = 'Estamos procesando el cobro. Solo asegúrate de tener saldo disponible en tu cuenta.';

export default function AccountBnplView(props: UserAccountComponent) {
  const classes = accountStyles();
  const { token } = props;

  const { loanData } = getLoan(token, true);
  const {
    purchasingPowerData,
    purchasingPowerLoading,
    purchasingPowerError,
    mutate,
  } = getBnplPurchasingPower(token);
  const { coupons } = getUserCoupons(token);

  const bnplLoans = loanData && loanData.filter((loan: LoanOverViewType) => loan.productType === 'bnpl');
  const currentDebt = bnplLoans && bnplLoans
    .reduce(
      (acc, loan) => acc
      + (loan.unpaidAmount + loan.unpaidDelinquenciesAmount + loan.unpaidCollectionFeesAmount),
      0,
    );
  const totalToPay = bnplLoans && bnplLoans
    .reduce((acc, loan) => acc + loan.totalAmount, 0);

  const paidAmount = (totalToPay && currentDebt !== null)
    ? Math.max(0, totalToPay - currentDebt) : null;

  const nextPayment = bnplLoans ? getBnplNextPayment(bnplLoans) : null;

  const hasNoActiveBnpl = !bnplLoans || bnplLoans.length === 0;
  const hasNoActiveButHasHistory = hasNoActiveBnpl
    && purchasingPowerData
    && (purchasingPowerData.totalHistoricalSpent ?? 0) > 0;

  const summaryPaidAmount = nextPayment !== null
    ? paidAmount
    : (purchasingPowerData?.totalHistoricalPaid ?? null);
  const summaryReferenceAmount = nextPayment !== null
    ? totalToPay
    : (purchasingPowerData?.totalHistoricalSpent ?? null);

  React.useEffect(() => {
    if (purchasingPowerLoading === false) {
      if (purchasingPowerError) {
        analytics.page('PU - BNPL', 'ERROR CARGANDO PODER DE COMPRA BNPL');
      } else if (purchasingPowerData === null) {
        analytics.page('PU - BNPL', 'SIN REGISTRO BNPL');
      } else {
        analytics.page('PU - BNPL', 'COMPRAS BNPL');
      }
    }
  }, [purchasingPowerData]);

  return (
    <Grid container sx={bnplPageStyles.rootGrid}>
      <div className={classes.contentContainer}>
        <ViewTitle
          title="Compra en cuotas y sin tarjetas"
          color="secondary"
          variant="h4"
          customHeight="fit-content"
        />
        <Box sx={bnplPageStyles.outerBox}>
          <Box sx={bnplPageStyles.innerBox}>
            <BnplPurchasingPower
              purchasingPowerData={purchasingPowerData}
              purchasingPowerLoading={purchasingPowerLoading}
              purchasingPowerError={purchasingPowerError}
              mutate={mutate}
              sxProps={bnplPageStyles.childCardMaxWidth}
            />
            <MisCuponesCard coupons={coupons} sxProps={bnplPageStyles.childCardMaxWidth} />
            {(nextPayment !== null || hasNoActiveButHasHistory) && (
            <BnplNextMonthSummaryCard
              label={NEXT_PAYMENT_LABELS[nextPayment?.kind ?? 'nextMonth']}
              helperText={nextPayment?.kind === 'inProcess' ? IN_PROCESS_HELPER_TEXT : undefined}
              headlineAmount={nextPayment?.amount ?? 0}
              paidAmount={summaryPaidAmount}
              referenceTotalAmount={summaryReferenceAmount}
              sxProps={bnplPageStyles.childCardMaxWidth}
            />
            )}
            <Card sx={sx().card}>
              <Box sx={bnplPageStyles.activePurchasesHeaderRow}>
                <Typography variant="body2" fontWeight="bold">
                  {loanData ? 'Mis compras activas' : 'No tienes compras activas'}
                </Typography>
                {purchasingPowerData && (
                  <BnplMovements token={token} defaultExpanded />
                )}
              </Box>
              {bnplLoans && bnplLoans.map((loan: LoanOverViewType, index: number) => (
                <Box key={loan.loan.id} sx={bnplPageStyles.loanOverviewWrapper}>
                  <LoanOverview
                    token={token}
                    loanData={loan}
                    defaultExpanded={false}
                    minimalistView
                    index={index}
                  />
                </Box>

              ))}
            </Card>
          </Box>
        </Box>
      </div>
    </Grid>
  );
}
